import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
    Building2,
    CalendarDays,
    ExternalLink,
    Flame,
    Loader2,
    Package,
    Save,
    Tag,
    X,
} from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCurrentUser } from "~/lib/auth";
import { type CVERecord, useCVE, useRevertCVE, useUpdateCVE } from "~/lib/cves";
import { severityLabel, severityPillClass } from "~/lib/iocVisuals";

import { CopyButton } from "./CopyButton";

interface Props {
    cveId: string | null;
    onClose: () => void;
}

export function CVEDetailDialog({ cveId, onClose }: Props): React.ReactElement | null {
    if (!cveId) return null;
    return <Inner cveId={cveId} onClose={onClose} />;
}

function Inner({ cveId, onClose }: { cveId: string; onClose: () => void }): React.ReactElement {
    const me = useCurrentUser();
    const canEdit = me.data?.roles.includes("admin") || me.data?.roles.includes("operator");
    const detail = useCVE(cveId);
    const update = useUpdateCVE();
    const revert = useRevertCVE();
    const cve = detail.data;

    // Brand-override diff vs. source-captured snapshot. "Overridden" = the
    // source_values payload has a value for the field AND it differs from
    // what's live on the row today. Undefined source keys mean we don't
    // know the original (no snapshot yet) and we hide the chip.
    const src = cve?.sourceValues ?? {};
    const sevOverridden =
        cve !== undefined && src.severity !== undefined && src.severity !== cve.severity;
    const tagsSorted = (a: string[] = []) => [...a].sort().join("|");
    const tagsOverridden =
        cve !== undefined &&
        src.tags !== undefined &&
        tagsSorted(src.tags) !== tagsSorted(cve.tags);

    async function doRevert(fields: string[]): Promise<void> {
        if (!cve) return;
        try {
            const res = await revert.mutateAsync({ id: cve.id, fields });
            // Re-sync local edit buffers so the pending-changes banner
            // clears along with the server state.
            setEditingSeverity(res.cve.severity);
            setEditingTags(res.cve.tags);
            toast.success(`Reverted ${fields.join(", ")} to source`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Revert failed");
        }
    }

    const [editingTags, setEditingTags] = useState<string[] | null>(null);
    const [editingSeverity, setEditingSeverity] = useState<number | null>(null);
    const [tagInput, setTagInput] = useState("");

    useEffect(() => {
        if (cve) {
            setEditingTags(cve.tags);
            setEditingSeverity(cve.severity);
        }
    }, [cve?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const dirty = useMemo(() => {
        if (!cve || editingTags === null || editingSeverity === null) return false;
        if (editingSeverity !== cve.severity) return true;
        const a = [...cve.tags].sort().join("|");
        const b = [...editingTags].sort().join("|");
        return a !== b;
    }, [cve, editingTags, editingSeverity]);

    async function save(): Promise<void> {
        if (!cve) return;
        try {
            await update.mutateAsync({
                id: cve.id,
                severity: editingSeverity ?? undefined,
                tags: editingTags ?? undefined,
            });
            toast.success("CVE updated");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Update failed");
        }
    }

    function addTag(t: string): void {
        const v = t.trim();
        if (!v || (editingTags ?? []).includes(v)) return;
        setEditingTags([...(editingTags ?? []), v]);
        setTagInput("");
    }
    function removeTag(t: string): void {
        setEditingTags((editingTags ?? []).filter((x) => x !== t));
    }

    const references = cve ? buildReferences(cve) : [];

    return (
        <Dialog.Root open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content dialog-content--lg enter-up" style={{ padding: 0 }}>
                        {/* Hero */}
                        <div
                            style={{
                                padding: "var(--space-6)",
                                borderBottom: "var(--border-hairline) solid var(--color-border)",
                            }}
                        >
                            <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                {cve ? (
                                    <div className="cluster-xs" style={{ color: "var(--color-danger)" }}>
                                        <Flame size={18} aria-hidden />
                                        <span className="type-caption">CVE · Known Exploited</span>
                                    </div>
                                ) : <span className="type-caption">Loading…</span>}
                                <Dialog.Close asChild>
                                    <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                        <X size={14} />
                                    </button>
                                </Dialog.Close>
                            </div>
                            {cve && (
                                <>
                                    <div
                                        className="cluster-xs"
                                        style={{ marginTop: "var(--space-3)", gap: "var(--space-3)", flexWrap: "wrap" }}
                                    >
                                        <span
                                            className="type-mono"
                                            style={{ fontSize: "var(--font-size-22)", fontWeight: 600 }}
                                        >
                                            {cve.cveId}
                                        </span>
                                        <CopyButton value={cve.cveId} />
                                        {cve.ransomwareUse && (
                                            <span className="chip chip--danger" style={{ height: 22 }}>
                                                ransomware
                                            </span>
                                        )}
                                        <span className={`status-pill ${severityPillClass(cve.severity)}`}>
                                            {cve.severity} · {severityLabel(cve.severity)}
                                        </span>
                                    </div>
                                    <Dialog.Title
                                        className="type-h2"
                                        style={{ marginTop: "var(--space-3)" }}
                                    >
                                        {cve.name || "—"}
                                    </Dialog.Title>
                                </>
                            )}
                        </div>

                        {detail.isLoading && (
                            <div className="empty-state" style={{ padding: "var(--space-12)" }}>
                                <Loader2 size={20} className="empty-state__icon animate-spin" aria-hidden />
                                <div className="empty-state__body">Loading details…</div>
                            </div>
                        )}

                        {cve && editingTags !== null && editingSeverity !== null && (
                            <div className="stack-lg" style={{ padding: "var(--space-6)" }}>
                                {/* Description */}
                                {cve.shortDescription && (
                                    <section className="stack-sm">
                                        <h3 className="type-h3">Description</h3>
                                        <p
                                            className="text-13 text-text-muted"
                                            style={{ lineHeight: "var(--line-height-relaxed)" }}
                                        >
                                            {cve.shortDescription}
                                        </p>
                                    </section>
                                )}

                                {/* Facts */}
                                <section
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                        gap: "var(--space-3)",
                                    }}
                                >
                                    <Fact icon={<Building2 size={14} />} label="Vendor" value={cve.vendorProject || "—"} />
                                    <Fact icon={<Package size={14} />} label="Product" value={cve.product || "—"} />
                                    <Fact
                                        icon={<CalendarDays size={14} />}
                                        label="Added to KEV"
                                        value={cve.dateAdded ?? "—"}
                                    />
                                    <Fact
                                        icon={<CalendarDays size={14} />}
                                        label="Remediation due"
                                        value={cve.dueDate ?? "—"}
                                    />
                                </section>

                                {/* CWEs */}
                                {cve.cwes.length > 0 && (
                                    <section className="card stack-sm">
                                        <h3 className="type-h3">Weakness classification</h3>
                                        <div className="cluster-xs">
                                            {cve.cwes.map((w) => (
                                                <a
                                                    key={w}
                                                    href={cweUrl(w)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="chip"
                                                    style={{ textDecoration: "none", color: "var(--color-accent)" }}
                                                >
                                                    {w.toUpperCase()}{" "}
                                                    <ExternalLink size={10} aria-hidden style={{ opacity: 0.6 }} />
                                                </a>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* References */}
                                <section className="card stack-sm">
                                    <h3 className="type-h3">References</h3>
                                    <div className="stack-xs">
                                        {references.map((r) => (
                                            <a
                                                key={r.href}
                                                href={r.href}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="cluster-xs text-13"
                                                style={{ color: "var(--color-accent)" }}
                                            >
                                                <ExternalLink size={12} aria-hidden />
                                                <span>{r.label}</span>
                                            </a>
                                        ))}
                                    </div>
                                </section>

                                {/* Severity editor */}
                                <section className="card stack-sm">
                                    <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                        <h3 className="type-h3">Severity</h3>
                                        <span className="text-12 text-text-muted">
                                            1–10 ·{" "}
                                            {cve.ransomwareUse
                                                ? "KEV + ransomware (source floor = 9)"
                                                : "KEV (source floor = 7)"}
                                        </span>
                                    </div>
                                    {sevOverridden && (
                                        <div className="cluster-xs" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                                            <span className="chip chip--info" style={{ height: 20 }}>
                                                overridden · source: <code>{String(src.severity)}</code>
                                            </span>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="btn btn--ghost btn--sm"
                                                    onClick={() => doRevert(["severity"])}
                                                    disabled={revert.isPending}
                                                >
                                                    Revert
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <SeverityScale
                                        value={editingSeverity}
                                        disabled={!canEdit}
                                        onChange={setEditingSeverity}
                                    />
                                </section>

                                {/* Tags */}
                                <section className="card stack-sm">
                                    <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                                        <h3 className="type-h3">
                                            <Tag size={14} className="text-accent" style={{ display: "inline", marginRight: 6 }} />
                                            Tags
                                        </h3>
                                        <span className="text-12 text-text-muted">{editingTags.length}</span>
                                    </div>
                                    {tagsOverridden && (
                                        <div className="cluster-xs" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                                            <span className="chip chip--info" style={{ height: 20 }}>
                                                overridden · source: <code>{(src.tags ?? []).join(", ") || "∅"}</code>
                                            </span>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="btn btn--ghost btn--sm"
                                                    onClick={() => doRevert(["tags"])}
                                                    disabled={revert.isPending}
                                                >
                                                    Revert
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {tagsOverridden && (src.tags ?? []).filter((t) => !cve.tags.includes(t)).length > 0 && (
                                        <div className="tag-stack">
                                            {(src.tags ?? [])
                                                .filter((t) => !cve.tags.includes(t))
                                                .map((t) => (
                                                    <span
                                                        key={"src-" + t}
                                                        className="chip"
                                                        title="Source-supplied tag removed by an operator"
                                                        style={{
                                                            textDecoration: "line-through",
                                                            opacity: 0.55,
                                                        }}
                                                    >
                                                        {t}
                                                    </span>
                                                ))}
                                        </div>
                                    )}
                                    <div className="tag-stack">
                                        {editingTags.map((t) => {
                                            const isOperatorAdded =
                                                src.tags !== undefined && !(src.tags ?? []).includes(t);
                                            return (
                                            <span
                                                key={t}
                                                className="chip"
                                                style={{ paddingRight: 4 }}
                                                title={isOperatorAdded ? "Operator-added (not in source)" : undefined}
                                            >
                                                {isOperatorAdded && (
                                                    <span
                                                        aria-hidden
                                                        style={{
                                                            marginRight: 3,
                                                            color: "var(--color-accent)",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        +
                                                    </span>
                                                )}
                                                {t}
                                                {canEdit && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTag(t)}
                                                        aria-label={`Remove ${t}`}
                                                        style={{
                                                            marginLeft: 4,
                                                            border: 0,
                                                            background: "transparent",
                                                            cursor: "pointer",
                                                            color: "currentColor",
                                                            opacity: 0.7,
                                                            padding: 0,
                                                            display: "inline-flex",
                                                        }}
                                                    >
                                                        <X size={11} />
                                                    </button>
                                                )}
                                            </span>
                                            );
                                        })}
                                        {editingTags.length === 0 && (
                                            <span className="text-text-subtle text-12">No tags.</span>
                                        )}
                                    </div>
                                    {canEdit && (
                                        <div className="cluster-xs">
                                            <input
                                                className="input"
                                                placeholder="Add tag…"
                                                value={tagInput}
                                                onChange={(e) => setTagInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        addTag(tagInput);
                                                    }
                                                }}
                                                style={{ flex: 1 }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn--secondary btn--sm press-crush"
                                                onClick={() => addTag(tagInput)}
                                                disabled={!tagInput.trim()}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    )}
                                </section>
                            </div>
                        )}

                        {/* Save bar */}
                        {cve && canEdit && (
                            <div
                                style={{
                                    position: "sticky",
                                    bottom: 0,
                                    background: "var(--color-surface-raised)",
                                    borderTop: "var(--border-hairline) solid var(--color-border)",
                                    padding: "var(--space-3) var(--space-6)",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "var(--space-3)",
                                }}
                            >
                                <span className="text-12 text-text-muted">
                                    {dirty ? "Unsaved changes" : "Up to date"}
                                </span>
                                <div className="cluster-xs">
                                    <button
                                        type="button"
                                        className="btn btn--ghost"
                                        onClick={() => {
                                            setEditingTags(cve.tags);
                                            setEditingSeverity(cve.severity);
                                        }}
                                        disabled={!dirty}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--primary press-crush"
                                        onClick={save}
                                        disabled={!dirty || update.isPending}
                                    >
                                        {update.isPending ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <Save size={14} />
                                        )}
                                        <span>{update.isPending ? "Saving…" : "Save"}</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function Fact({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
}): React.ReactElement {
    return (
        <div
            className="surface"
            style={{ padding: "var(--space-3)", display: "flex", flexDirection: "column", gap: 4 }}
        >
            <div className="cluster-xs text-text-subtle">
                {icon}
                <span className="type-caption">{label}</span>
            </div>
            <div className="text-13">{value}</div>
        </div>
    );
}

interface SeverityScaleProps {
    value: number;
    disabled?: boolean;
    onChange: (v: number) => void;
}

function SeverityScale({ value, disabled, onChange }: SeverityScaleProps): React.ReactElement {
    return (
        <div className="cluster-xs" style={{ gap: 4 }}>
            {Array.from({ length: 10 }, (_, i) => {
                const n = i + 1;
                const active = n <= value;
                const cls = severityPillClass(n);
                return (
                    <button
                        key={n}
                        type="button"
                        disabled={disabled}
                        aria-label={`Set severity to ${n}`}
                        onClick={() => onChange(n)}
                        className={active ? cls : ""}
                        style={{
                            width: 32,
                            height: 28,
                            border: active ? "0" : "var(--border-hairline) solid var(--color-border)",
                            borderRadius: "var(--radius-sm)",
                            cursor: disabled ? "not-allowed" : "pointer",
                            color: active ? undefined : "var(--color-text-muted)",
                            background: active ? undefined : "transparent",
                            fontFamily: "var(--font-family-mono)",
                            fontSize: "var(--font-size-12)",
                            transition: "all 80ms var(--ease-standard)",
                        }}
                    >
                        {n}
                    </button>
                );
            })}
        </div>
    );
}

interface Reference {
    href: string;
    label: string;
}

/** Build canonical external references for a CVE id. We construct these
 * client-side so a new ingest source doesn't need to re-emit them. */
function buildReferences(cve: CVERecord): Reference[] {
    const id = cve.cveId;
    return [
        { href: `https://nvd.nist.gov/vuln/detail/${id}`, label: "NVD — National Vulnerability Database" },
        { href: `https://cve.mitre.org/cgi-bin/cvename.cgi?name=${id}`, label: "MITRE CVE entry" },
        {
            href: "https://www.cisa.gov/known-exploited-vulnerabilities-catalog",
            label: "CISA KEV catalog",
        },
        { href: `https://www.google.com/search?q=${encodeURIComponent(id)}`, label: "Google search" },
    ];
}

function cweUrl(cwe: string): string {
    const num = cwe.replace(/^cwe-/i, "");
    return `https://cwe.mitre.org/data/definitions/${num}.html`;
}
